import { A11yModule } from '@angular/cdk/a11y';
import { DecimalPipe, PercentPipe } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CurrencyFormatPipe } from '@softline/ui-core';
import { BadgeComponent } from '@softline/ui-core';
import { ButtonComponent } from '@softline/ui-core';
import {
  CardBodyComponent,
  CardComponent,
  CardFooterComponent,
  CardHeaderComponent,
  CardLeadingComponent,
  CardTitleComponent,
  CardTrailingComponent,
} from '@softline/ui-core';
import { DsProgressBarComponent } from '../ds-progress-bar/ds-progress-bar.component';
import { DsProgressBarSegment } from '../ds-progress-bar/ds-progress-bar.types';
import { IconComponent } from '@softline/ui-core';
import { LoadingSkeletonComponent } from '@softline/ui-core';
import { AnimatedNumberDirective } from './animated-number.directive';
import { DeltaFlipDirective } from './delta-flip.directive';
import {
  KpiColor,
  KpiCurrencyCode,
  KpiCurrencySymbol,
  KpiDensity,
  KpiFormat,
  KpiVariant,
} from './kpi.types';

/** Design-System KPI tile. Renders a headline metric with an animated
    number, an optional period-over-period delta badge, and an optional
    progress bar (0-150 %) with a tick at the 100 % reference marker. */
@Component({
  selector: 'soft-kpi, soft-ds-kpi',
  imports: [
    CardComponent,
    CardHeaderComponent,
    CardLeadingComponent,
    CardTitleComponent,
    CardTrailingComponent,
    CardBodyComponent,
    CardFooterComponent,
    BadgeComponent,
    ButtonComponent,
    DsProgressBarComponent,
    IconComponent,
    LoadingSkeletonComponent,
    AnimatedNumberDirective,
    DeltaFlipDirective,
    A11yModule,
    DecimalPipe,
  ],
  providers: [CurrencyFormatPipe, DecimalPipe, PercentPipe],
  templateUrl: './kpi.component.html',
  styleUrl: './kpi.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.density-compact]': "density() === 'compact'",
  },
})
export class KpiComponent {
  readonly title = input.required<string>();
  readonly value = input.required<number>();

  readonly prevValue = input<number | null>(null);

  readonly format = input<KpiFormat>('currency');

  readonly currencyCode = input<KpiCurrencyCode>('EUR');
  readonly currencySymbol = input<KpiCurrencySymbol>('code');

  /** Custom formatter that overrides `format` / `currencyCode` /
      `currencySymbol`. Use when the built-in formats don't fit (e.g.
      "EUR 1,234.56" with a non-default thousands separator, "12.3 %"
      with a trailing space). */
  readonly formatFn = input<((value: number) => string) | null>(null);

  readonly showBadge = input<boolean>(true);

  /** When false, the headline value renders changes instantly with no
      tween. Use for live tickers (clocks, timers). */
  readonly animateValue = input<boolean>(true);

  /** Custom formatter for the delta badge text. Receives the delta percent
      (signed), the current value, and the previous/target value. When set,
      replaces the default `±x.x%` rendering. Use for absolute deltas
      (e.g. `+4:00 Std` over a target). */
  readonly badgeFormatFn = input<
    ((deltaPercent: number, value: number, prevValue: number) => string) | null
  >(null);

  /** Which auxiliary visualization to render below the headline value:
      - `'number'`: just the value (and optional comparison line)
      - `'bar'` *(default)*: 0-150 % progress bar with 100 % reference tick
      - `'sparkline'`: inline SVG trend line — requires `sparkline` input */
  readonly variant = input<KpiVariant>('bar');

  /** Spacing/typography density. `'compact'` tightens padding and shrinks the
      value/title for dense layouts (e.g. side-panel KPI tiles). */
  readonly density = input<KpiDensity>('comfortable');

  /** Accent color for the leading icon, progress bar, and sparkline.
      `'auto'` (default) keeps the original behavior — green when the trend
      is positive, red when negative. Any explicit color overrides that. */
  readonly color = input<KpiColor>('auto');

  /** Flips the good/bad color semantics: a rising value renders red, a
      falling one green. Arrows and signs still follow the real direction —
      only the colors invert. For metrics where growth is bad (inventory
      value, costs, error counts). */
  readonly invertDelta = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });

  /** Optional Font Awesome class string rendered as a leading icon
      badge in the header (e.g. `'fa-regular fa-cart-shopping'`). */
  readonly icon = input<string | null>(null);

  /** When `true`, replaces the live content with a shimmering skeleton
      that mirrors the final layout (title, badge slot, value, comparison
      line, progress bar). */
  readonly loading = input<boolean>(false);

  /** Optional absolute difference value shown to the right of the comparison
      label (e.g. "+12.345 €"). Formatted with the same `formatFn` / `format`
      as the headline value. Pass `null` (default) to hide. */
  readonly difference = input<number | null>(null);

  readonly comparisonLabel = input<string>('Comparison');
  /** Separator rendered between `comparisonLabel` and the previous value.
      Defaults to `':'` (e.g. `Comparison: 60.207`). Set to `''` for layouts
      like `/ 60.207` where the label itself is a punctuation glyph. */
  readonly comparisonSeparator = input<string>(':');
  /** Layout of the comparison line relative to the value.
      `'below'` (default) stacks them in a column. `'inline'` puts them on
      the same row (e.g. `23.557 / 60.207`). */
  readonly comparisonPosition = input<'below' | 'inline'>('below');
  /** What the comparison line shows:
      - `'value'` *(default)*: the previous/target value (`label: prevValue`).
      - `'delta'`: the signed percentage change vs. `prevValue`, colored
        green (up) / red (down) with a ▲/▼ marker, followed by the label
        (e.g. `▲ +8,4 % ggü. Vorjahr`). */
  readonly comparisonMode = input<'value' | 'delta'>('value');
  readonly progressLabel = input<string>('reached');

  /** Horizontal scale for the progress bar. Default `1` fills the bar to
      the right edge at 100% and hides the reference marker. Set to
      `100 / 150` (or similar) to reserve headroom for overshoot past 100%
      and place a 100% reference tick at `scale * 100` of the visible track. */
  readonly progressScale = input<number>(1);
  /** Upper bound for the rendered progress value. Defaults to `100 / scale`
      so e.g. `progressScale = 100/150` caps at 150, and `progressScale = 1`
      caps at 100. */
  readonly progressMax = input<number | null>(null);

  /** Trend series for the `'sparkline'` variant. Hidden when `null` or
      fewer than two points. */
  readonly sparkline = input<readonly number[] | null>(null);

  /** Optional trend series that turns the built-in variant toggle into a
      bar ↔ sparkline flip: with `variant='bar'` and this set (≥ 2 points),
      the card's header toggle switches the footer between the progress bar
      and this trend line. Independent of the segments toggle. */
  readonly sparklineAlternative = input<readonly number[] | null>(null);

  /** Show the bar ↔ sparkline toggle button immediately, before trend data
      arrives. With `variant='bar'` and this `true`, the header toggle renders
      as soon as the card does; the sparkline itself still only draws once
      `sparklineAlternative` has ≥ 2 points (until then the flipped footer
      keeps the progress bar). Default `false` keeps the data-gated behavior. */
  readonly showSparklineToggle = input<boolean>(false);

  /** Segments for the `'segmented'` variant — drives both the segmented
      progress bar and its built-in legend. Each item: `{ value, color?,
      severity?, label? }`. Forwarded directly to `<soft-ds-progress-bar>`. */
  readonly segments = input<readonly DsProgressBarSegment[]>([]);

  /** Optional alternative segment set. When provided alongside `segments`,
      the variant toggle flips between two segmented views (segments ↔
      segmentsAlternative) instead of the default bar ↔ segmented flip. Useful
      for cards that always show a bar+legend but expose two different
      breakdowns (e.g. status vs aging). */
  readonly segmentsAlternative = input<readonly DsProgressBarSegment[] | null>(null);

  /** Optional alternative headline value, paired with `segmentsAlternative`.
      When the toggle is showing the alternative dataset, KPI displays this
      value (and `prevValueAlternative`) instead of `value`/`prevValue`. */
  readonly valueAlternative = input<number | null>(null);
  readonly prevValueAlternative = input<number | null>(null);
  /** Optional alternative comparison label — shown next to `valueAlternative`
      when the toggle is active. Lets the alt-view annotate its number (e.g.
      "Gesamt") even when no `prevValueAlternative` is set. */
  readonly comparisonLabelAlternative = input<string | null>(null);

  protected readonly hasAlternativeSegments = computed(
    () => (this.segmentsAlternative()?.length ?? 0) > 0
  );

  /** True when the toggle is on AND alternative data is available. */
  private readonly showingAlternative = computed(
    () => this.hasAlternativeSegments() && this.showAlternative()
  );

  /** The segments currently shown when the variant is `'segmented'` — picks
      between primary and alternative based on the toggle state. */
  protected readonly resolvedSegments = computed<readonly DsProgressBarSegment[]>(() => {
    if (this.showingAlternative()) {
      return this.segmentsAlternative() ?? this.segments();
    }
    return this.segments();
  });

  /** The headline value currently shown — swaps to `valueAlternative` when
      the toggle is active (falls back to primary `value` if not set). */
  protected readonly resolvedValue = computed<number>(() => {
    if (this.showingAlternative()) {
      const alt = this.valueAlternative();
      if (alt !== null) return alt;
    }
    return this.value();
  });

  /** Comparison/prev value paired with `resolvedValue`. When the toggle is
      on, returns `prevValueAlternative` verbatim (including `null` — which
      hides the comparison line in alt mode unless an alt label is set). */
  protected readonly resolvedPrevValue = computed<number | null>(() => {
    if (this.showingAlternative()) {
      return this.prevValueAlternative();
    }
    return this.prevValue();
  });

  /** Comparison label currently shown — swaps to `comparisonLabelAlternative`
      when the toggle is active. */
  protected readonly resolvedComparisonLabel = computed<string>(() => {
    if (this.showingAlternative()) {
      const alt = this.comparisonLabelAlternative();
      if (alt !== null) return alt;
    }
    return this.comparisonLabel();
  });

  /** Cleared / "all done" state. When true, the KPI body shows a green
      check-icon headline + summary instead of the numeric value and any
      progress visuals. Works across all variants. */
  readonly cleared = input<boolean>(false);
  readonly clearedLabel = input<string>('Alles erledigt');
  readonly clearedSummary = input<string | null>(null);

  /** True when a bar ↔ sparkline flip is DATA-READY: `variant='bar'` plus a
      valid `sparklineAlternative` series (≥ 2 points). Governs the actual
      variant flip so the footer never opens onto an empty chart. */
  protected readonly hasSparklineAlternative = computed(
    () => this.variant() === 'bar' && (this.sparklineAlternative()?.length ?? 0) >= 2
  );

  /** True when the card is in bar↔sparkline mode — either the flip is
      data-ready, or the consumer opted the toggle in early (`showSparklineToggle`).
      Drives the toggle button's presence + glyph independently of data arrival,
      so the affordance shows at first render (see `showSparklineToggle`). */
  protected readonly isSparklineToggleMode = computed(
    () =>
      this.variant() === 'bar' &&
      (this.hasSparklineAlternative() || this.showSparklineToggle())
  );

  /** The toggle button auto-renders whenever an alternative view is available
      — alternative segments, a data-ready sparkline flip, or an opted-in
      (early) sparkline toggle. */
  protected readonly shouldShowToggle = computed(
    () => this.hasAlternativeSegments() || this.isSparklineToggleMode()
  );

  /** When true, the whole card becomes clickable (cursor + role=button) and
      emits `(cardClick)` on click. Lets consumers attach navigation without
      nesting a `<button>` around the KPI (which would conflict with the
      inner variant toggle button). */
  readonly clickable = input<boolean>(false);
  readonly cardClick = output<void>();

  /** When true, each segment in the segmented bar and its legend becomes
      individually clickable; `(segmentClick)` emits the segment object. Card
      `(cardClick)` is suppressed when a segment is clicked (event stops at
      the inner button). */
  readonly clickableSegments = input<boolean>(false);
  readonly segmentClick = output<DsProgressBarSegment>();

  /** Optional short text (1-4 chars) rendered as a tinted leading pill —
      e.g. document-type abbreviations like "OF", "RAU", "MLF". When set,
      this wins over `icon`. Provide `accent` to color the pill (defaults
      to the resolved KPI accent). */
  readonly abbreviation = input<string | null>(null);

  protected readonly hasLeading = computed(() => !!this.abbreviation() || !!this.icon());

  protected onCardClick(): void {
    if (this.clickable()) this.cardClick.emit();
  }

  /** Toggle state for the alternative-segments case — tracks which dataset
      is currently shown. */
  private readonly showAlternative = signal<boolean>(false);

  /** Resolved effective variant. When a bar ↔ sparkline flip is active, the
      toggle overrides `variant='bar'` with `'sparkline'`; otherwise it is just
      the `variant` input (the segments toggle swaps datasets, not variants). */
  protected readonly effectiveVariant = computed<KpiVariant>(() =>
    this.hasSparklineAlternative() && this.showAlternative()
      ? 'sparkline'
      : this.variant()
  );

  /** Series fed to the sparkline SVG — the alternative trend when the bar ↔
      sparkline toggle is active, else the plain `sparkline` input. */
  protected readonly resolvedSparkline = computed<readonly number[] | null>(() =>
    this.hasSparklineAlternative() && this.showAlternative()
      ? this.sparklineAlternative()
      : this.sparkline()
  );

  /** Whether the toggle is showing the alternative dataset. */
  protected readonly isToggleActive = computed(() => this.showAlternative());

  /** Toggle button glyph — trend/bar for the sparkline flip, list/chart for
      the segments flip. */
  protected readonly toggleIcon = computed(() => {
    if (this.isSparklineToggleMode()) {
      return this.isToggleActive()
        ? 'fa-regular fa-bar-progress'
        : 'fa-regular fa-chart-line';
    }
    return this.isToggleActive() ? 'fa-regular fa-list-ul' : 'fa-regular fa-chart-simple';
  });

  protected readonly toggleAriaLabel = computed(() => {
    if (this.isSparklineToggleMode()) {
      return this.isToggleActive() ? 'Zielerreichung anzeigen' : 'Verlauf anzeigen';
    }
    return this.isToggleActive() ? 'Wert anzeigen' : 'Aufteilung anzeigen';
  });

  protected toggleVariant(): void {
    this.showAlternative.update((v) => !v);
  }

  /** Per-instance gradient id so multiple KPI tiles on the same page
      don't share a single `<linearGradient>` definition. */
  protected readonly gradientId = `ds-kpi-spark-${Math.random().toString(36).slice(2, 9)}`;

  private readonly currencyPipe = inject(CurrencyFormatPipe);
  private readonly decimalPipe = inject(DecimalPipe);

  readonly formatNumber = computed<(value: number) => string>(() => {
    const custom = this.formatFn();
    if (custom) return custom;
    const fmt = this.format();
    const code = this.currencyCode();
    const symbol = this.currencySymbol();
    return (value: number): string => {
      switch (fmt) {
        case 'integer':
          return this.decimalPipe.transform(value, '1.0-0') ?? '';
        case 'decimal':
          return this.decimalPipe.transform(value, '1.0-2') ?? '';
        case 'currency':
        default:
          return this.currencyPipe.transform(value, code, symbol) ?? '';
      }
    };
  });

  readonly deltaPercent = computed<number | null>(() => {
    const prev = this.resolvedPrevValue();
    if (prev === null || prev === undefined || prev === 0) return null;
    return ((this.resolvedValue() - prev) / prev) * 100;
  });

  /** Whether the delta is non-negative (drives the up/down glyph). */
  readonly deltaIsUp = computed<boolean>(() => (this.deltaPercent() ?? 0) >= 0);

  /** Whether the delta reads as a good outcome. Note the comparison is against
      zero on both sides: an unchanged value is never "bad", not even inverted. */
  readonly deltaIsGood = computed<boolean>(() => {
    const delta = this.deltaPercent() ?? 0;
    return this.invertDelta() ? delta <= 0 : delta >= 0;
  });

  /** Good/bad polarity of the absolute `difference` line, same zero handling. */
  protected readonly differenceIsGood = computed<boolean>(() => {
    const difference = this.difference() ?? 0;
    return this.invertDelta() ? difference <= 0 : difference >= 0;
  });

  /** Formatted signed delta for the `'delta'` comparison mode, e.g. `▲ +8,4 %`. */
  readonly deltaText = computed<string>(() => {
    const delta = this.deltaPercent();
    if (delta === null) return '';
    const arrow = delta >= 0 ? '▲' : '▼';
    const sign = delta >= 0 ? '+' : '';
    const formatted = this.decimalPipe.transform(delta, '1.2-2') ?? '';
    return `${arrow} ${sign}${formatted} %`;
  });

  readonly progressPercent = computed<number | null>(() => {
    const prev = this.resolvedPrevValue();
    if (prev === null || prev === undefined || prev === 0) return null;
    return (this.resolvedValue() / prev) * 100;
  });

  /** Upper bound for the rendered progress value. Derived from `progressMax`
      if set, otherwise from `progressScale` (e.g. scale `100/150` → cap 150).
      With the default scale of 1, the cap is 100 (no overshoot rendered). */
  protected readonly progressCap = computed<number>(() => {
    const max = this.progressMax();
    if (max !== null && max > 0) return max;
    return 100 / this.progressScale();
  });

  readonly progressClamped = computed<number>(() => {
    const p = this.progressPercent() ?? 0;
    if (p < 0) return 0;
    const cap = this.progressCap();
    return p > cap ? cap : p;
  });

  readonly progressOnTrack = computed(() => (this.progressPercent() ?? 0) >= 100);

  readonly displayPrevValue = computed(() => {
    const prev = this.resolvedPrevValue();
    return prev === null || prev === undefined ? '' : this.formatNumber()(prev);
  });

  readonly displayDifference = computed<string | null>(() => {
    const diff = this.difference();
    if (diff === null || diff === undefined) return null;
    const formatted = this.formatNumber()(Math.abs(diff));
    const sign = diff >= 0 ? '+' : '−';
    return `${sign}${formatted}`;
  });

  readonly hasComparison = computed(() => {
    const prev = this.resolvedPrevValue();
    if (prev !== null && prev !== undefined && prev !== 0) return true;
    // Alt mode with a label but no prev value — still show the label line.
    return this.showingAlternative() && this.comparisonLabelAlternative() !== null;
  });

  /** Sparkline geometry. Maps the series into a 100×32 viewBox with a 2px
      vertical inset so the curve doesn't clip the stroke. The line is drawn
      as a Catmull-Rom-style smoothed path (cubic Beziers) for the soft
      "wave" look — straight polylines feel jagged at small heights. */
  readonly sparklinePath = computed<{ line: string; area: string } | null>(() => {
    const series = this.resolvedSparkline();
    if (!series || series.length < 2) return null;
    const w = 100;
    const h = 32;
    const inset = 2;
    const min = Math.min(...series);
    const max = Math.max(...series);
    const range = max - min || 1;
    const stepX = w / (series.length - 1);
    const points = series.map((v, i) => {
      const x = i * stepX;
      const y = inset + (h - inset * 2) - ((v - min) / range) * (h - inset * 2);
      return [x, y] as const;
    });

    // Catmull-Rom → cubic Bezier with tension 0.5. Produces a smooth curve
    // that passes through every data point without overshooting.
    const tension = 0.5;
    let line = `M${points[0][0].toFixed(2)},${points[0][1].toFixed(2)}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i - 1] ?? points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2] ?? p2;
      const cp1x = p1[0] + ((p2[0] - p0[0]) / 6) * tension * 2;
      const cp1y = p1[1] + ((p2[1] - p0[1]) / 6) * tension * 2;
      const cp2x = p2[0] - ((p3[0] - p1[0]) / 6) * tension * 2;
      const cp2y = p2[1] - ((p3[1] - p1[1]) / 6) * tension * 2;
      line += ` C${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
    }
    const area = `${line} L${w},${h} L0,${h} Z`;
    return { line, area };
  });

  /** Whether the series ends higher than it starts. Drives the auto-color
      mode so the trend direction matches the delta badge. */
  readonly sparklineRising = computed<boolean>(() => {
    const series = this.resolvedSparkline();
    if (!series || series.length < 2) return true;
    return series[series.length - 1] >= series[0];
  });

  /** Whether the consumer set an explicit color (anything other than `'auto'`). */
  protected readonly hasExplicitColor = computed(() => this.color() !== 'auto');

  /** Resolved fill color for the progress bar — accent when the caller set
      a color, otherwise the standard on/off-track token. The `'gradient'`
      color is a special case: instead of a single color, it returns a
      `linear-gradient` whose stops are anchored to track-relative percentages
      (0%/50%/100%/150%) and remapped into the fill element's local
      coordinates so the visual gradient stays "in track space" as the fill
      width changes. Result: a thermometer that always shows dark red at the
      left of the track, amber at 50%, green at 100%, bright green at 150% —
      regardless of how full the bar currently is. */
  protected readonly progressFillColor = computed<string>(() => {
    if (this.color() === 'gradient') return this.gradientFill();
    if (this.hasExplicitColor()) return this.accentColor();
    // With `invertDelta`, passing 100 % of the reference is the bad outcome.
    return this.progressOnTrack() !== this.invertDelta()
      ? 'var(--ds-progress-bar-fill-on-track)'
      : 'var(--ds-progress-bar-fill-off-track)';
  });

  /** Computes the thermometer-style gradient. Stops are anchored to
      track-relative positions and divided by the actual fill width so the
      visual stays consistent as progress grows. The ramp is intentionally
      generous: amber starts at 30 % of target, green at 70 %, bright green
      at 150 %. So a card sitting at ~70 % of target already reads as "good
      enough" instead of still looking like a near-miss.

      When the fill is shorter than the first non-zero stop, the gradient
      collapses to a solid red — under-target stays red until it crosses
      the amber threshold. */
  private readonly gradientFill = computed<string>(() => {
    // Fill width in % of the track. `progressClamped` is in % of `prev`;
    // `progressScale` is the track scale (e.g. 100/150 for a 0-150% bar).
    const fillPercent = this.progressClamped() * this.progressScale();
    if (fillPercent <= 0) {
      return 'var(--ds-kpi-gradient-stop-min)';
    }
    // Remap track-relative stop positions into fill-local positions. A stop
    // at track 30% becomes (30 / fillPercent)% in fill-local coordinates.
    const remap = (trackPercent: number): number =>
      Math.min(100, (trackPercent / fillPercent) * 100);
    const midPercent = remap(30);
    const targetPercent = remap(70);
    const overshootPercent = remap(150);
    return `linear-gradient(
      to right,
      var(--ds-kpi-gradient-stop-min) 0%,
      var(--ds-kpi-gradient-stop-mid) ${midPercent.toFixed(2)}%,
      var(--ds-kpi-gradient-stop-target) ${targetPercent.toFixed(2)}%,
      var(--ds-kpi-gradient-stop-max) ${overshootPercent.toFixed(2)}%
    )`;
  });

  /** In `auto` mode, whether the trend is positive. Prefers the delta
      (which the user sees on the badge) and falls back to the sparkline
      direction when there's no `prevValue`. Defaults to true (green) when
      there's no signal at all. */
  private readonly autoRising = computed<boolean>(() => {
    const delta = this.deltaPercent();
    if (delta !== null) return delta >= 0;
    const series = this.resolvedSparkline();
    if (series && series.length >= 2) {
      return series[series.length - 1] >= series[0];
    }
    return true;
  });

  /** Whether there is a direction to judge at all. Without one the accent stays
      on its neutral default instead of being flipped to "bad" by `invertDelta`. */
  private readonly hasTrendSignal = computed<boolean>(
    () => this.deltaPercent() !== null || (this.resolvedSparkline()?.length ?? 0) >= 2
  );

  /** Resolved accent for the icon and sparkline. Defaults to the trend
      direction (success/danger) when `color === 'auto'`. For `'gradient'`,
      the icon/sparkline pick a single color from the same stop palette
      based on `progressOnTrack` (we can't paint a gradient onto a font
      glyph or sparkline). */
  protected readonly accentColor = computed<string>(() => {
    switch (this.color()) {
      case 'brand':
        return 'var(--ds-text-brand)';
      case 'primary':
        return 'var(--ds-text-primary)';
      case 'success':
        return 'var(--ds-text-status-success)';
      case 'danger':
        return 'var(--ds-text-status-danger)';
      case 'warning':
        return 'var(--ds-text-status-warning)';
      case 'info':
        return 'var(--ds-text-status-info)';
      case 'gradient':
        // Flip the icon/sparkline to green once we've crossed the gradient's
        // green stop (70 % of target). Keeps the icon in sync with the bar
        // — when the eye sees green on the bar, the icon also reads green.
        return (this.progressPercent() ?? 0) >= 70
          ? 'var(--ds-kpi-gradient-stop-target)'
          : 'var(--ds-kpi-gradient-stop-min)';
      case 'auto':
      default: {
        // With a delta, read the same polarity the badge shows — including its
        // zero handling. Without one, fall back to the sparkline direction, and
        // don't let the inversion turn "no signal" into "bad".
        if (this.deltaPercent() !== null) {
          return this.deltaIsGood()
            ? 'var(--ds-text-status-success)'
            : 'var(--ds-text-status-danger)';
        }
        const invert = this.invertDelta() && this.hasTrendSignal();
        return this.autoRising() !== invert
          ? 'var(--ds-text-status-success)'
          : 'var(--ds-text-status-danger)';
      }
    }
  });
}
